import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { MCard, MGrid, MSelect, MSpinner, MSplitter } from "components/Shared";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";
import "styles/components/CallCenter/PhoneSupportForm/PhoneSupportForm.scss"
import { TextField } from "react-md";
import { Button } from "react-md";
import { getAllRoleData } from "requests/internetUsers";
import { MCardControlMethods, MCardField } from "components/Shared/MCard";
import { SearchResultDto } from "interfaces/BusinessModelsExtensions";
import { CaseResult, EmployeeListDto, EmployeeRoleEnum, FieldServicesCaseResult, PhoneSupportCallAddRequest, PhoneSupportCallCaseAddRequest, PhoneSupportCategoryDto, QaAgentAuditResultResponse, RoleDto } from "interfaces/Mueller.Business.Models";
import { getEmployeesByRoles } from "requests/employee/index";
import { BUSOP_AGENT, FIELD_SERVICE_REP } from "typeConstants/employeeRoles";
import MCaseView from "containers/Homeowner/MCaseView";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { addPhoneSupportCall, getPhoneSupportCategories } from "requests/phoneSupportCall";
import { appendHeader, clearHeaderDefinitions } from "actions/layout";
import { connect } from "react-redux";
import * as yup from "yup";
import { addPhoneSupportCallCase } from "requests/phoneSupportCallCases";
import { normalizePhone } from "components/FormElements";
import { userHasRight } from "selectors/rights";
import { CLOCK_IN_OR_OUT_FUNCTION } from "typeConstants/rights";
import { InitialState } from "reducers/initialState";
import { getAssignedCasesByEmployeeId } from "requests/inspectorCases";
import { getUserAuditResults } from "requests/qaCaseAudit";
import { getCasesInQueue } from "requests/qualityAssurance";
import { COL_CODE_ADDRESS, COL_CODE_CONTROL_NUMBER, COL_CODE_CUSTOMER_NAME } from "components/Shared/MGrid";

interface Props extends RouteComponentProps  {
  appendHeader: (headerDefinitions: any) => void,
  clearHeaderDefinitions: (headerDefinitions: any) => void,
  canClockInOut: boolean,
  activeClock: InitialState["user"]["activeClock"],
};

// someone decided on the back end it would be a good idea to not place models in the model folder, so i am typing the parts of this that i need
// this lives at Orthos\Mueller\Mueller\Mueller.Business\BusinessLogic\SearchResultProviders\ViewModels\SearchBarResults\FieldLevelCoordinatorCase.cs
export interface FieldLevelCoordinatorCasePartial extends CaseResult {
  inspectionType: string,
  address: string,
  customerName: string,
};

export const roleToEmployeeRoleMap = [
  { roleName: "Inspector", employeeRoleCode: FIELD_SERVICE_REP },
  { roleName: "Quality Review", employeeRoleCode: BUSOP_AGENT },
  // { roleName: "Appraiser", employeeRoleCode: VALUATION_APPRAISER }, // commenting out in case someone wants it added back
];

const phoneSupportCategoryCodeBlacklist = [
  "COACHING_CALL",
];

const includedRoleNames = roleToEmployeeRoleMap.map(r => r.roleName);
const saveButtonForClear = { buttons: [{ name: "Save",}]}

const PhoneSupportForm = ({ appendHeader, clearHeaderDefinitions, history, location, canClockInOut, activeClock }: Props) => {
  const mounted = useRef(true);
  const handleSaveClicked = useRef<() => void>(null);
  const cardControlMethods = useRef<MCardControlMethods>();
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const historyBlockRef = useRef<Function>();

  // we need to be able to clear some data in the call card but cards werent built with that in mind while they are in edit mode
  // this allows us to randomize the card key when we need to update it, which will make it pull the data in its fields from the following state data again
  const [callCardKey, setCallCardKey] = useState("");

  const [roles, setRoles] = useState<RoleDto[]>(null);
  const [phoneSupportCategories, setPhoneSupportCategories] = useState<PhoneSupportCategoryDto[]>(null);
  const [employeesByEmployeeRoleId, setEmployeesByEmployeeRoleId] = useState<Record<EmployeeRoleEnum, EmployeeListDto[]>>();
  const [isSavingCall, setIsSavingCall] = useState(false);

  const [callRole, setCallRole] = useState<{ roleDto: RoleDto, employeeRoleCode: number}>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeListDto>(null);
  const [callerName, setCallerName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<PhoneSupportCategoryDto>(null);

  // the calls we call dont all use FieldServicesCaseResult, but they all use different formats, so i just save the columns i need in the format of FieldServicesCaseResult, not nearly all columns will be filled
  const [employeeCaseQueue, setEmployeeCaseQueue] = useState<FieldServicesCaseResult[]>(null);

  const [notes, setNotes] = useState("");
  const [startedWorkingOnCall, setStartedWorkingOnCall] = useState<Date>(null);

  const [controlNumberLookup, setControlNumberLookup] = useState("");
  const [searchForControlNumber, setSearchForControlNumber] = useState<number>(null);

  const [callCases, setCallCases] = useState<PhoneSupportCallCaseAddRequest[]>([]);
  
  let isLoadingCaseQueue = false;
  if (selectedEmployee && callRole && !employeeCaseQueue) isLoadingCaseQueue = true;

  const filterPhoneSupportCategories = (categories: PhoneSupportCategoryDto[]) => categories.filter(c => !phoneSupportCategoryCodeBlacklist.includes(c.code));

  useEffect(() => {
    getAllRoleData((roles: RoleDto[]) => setRoles(roles.filter(r => includedRoleNames.includes(r.name))), mounted);
    getEmployeesByRoles(
      roleToEmployeeRoleMap.map(r => r.employeeRoleCode),
      employeeList => setEmployeesByEmployeeRoleId(employeeList.filter((e: EmployeeListDto) => !!e.internetUserId).reduce((acc, e: EmployeeListDto) => {
        if (!acc[e.roleId]) acc[e.roleId] = [];
        acc[e.roleId].push(e);
        return acc;
      }, {})),
      mounted);
    getPhoneSupportCategories(c => setPhoneSupportCategories(filterPhoneSupportCategories(c)), mounted);

    clearHeaderDefinitions(saveButtonForClear);
    appendSaveHeaderButton();

    let needsClockIn = canClockInOut && !(activeClock?.clockInDate);

    if (needsClockIn) {
      dialog.show(
        "Please Clock In",
        "You are not currently clocked in. Calls cannot be logged until you are clocked in. Please navigate to the QA Dashboard to clock in before starting your Call",
        [{ label: "QA Dashboard", onClick: () => history.push("/qa-dashboard"), isPrimary: true }],
        { width: 500 }
      );
    }

    return () => {
      mounted.current = false;
      clearHeaderDefinitions(saveButtonForClear);
      setHasChangedData(false);
    };
  }, []);

  useEffect(() => {
    // if statement avoids this getting called on first render
    if (callRole || selectedEmployee || callerName || selectedCategory || notes || searchForControlNumber) {
      setHasChangedData(true);
    }
  }, [callRole, selectedEmployee, callerName, selectedCategory, notes, searchForControlNumber]);

  useEffect(() => {
    setEmployeeCaseQueue(null);
    if (selectedEmployee && callRole) getAssignedCases();
  }, [selectedEmployee?.employeeId, callRole?.employeeRoleCode]);

  const caseSort = (c1: FieldServicesCaseResult, c2: FieldServicesCaseResult) => c1.controlIDNumber - c2.controlIDNumber;

  const getAssignedCases = () => {
    if (callRole.roleDto.name === "Inspector") {
      getAssignedCasesByEmployeeId(selectedEmployee.employeeId, (result: SearchResultDto<FieldLevelCoordinatorCasePartial>) => {
        let cases = result.rows.map(c => ({
          controlIDNumber: c.controlIdNumber,
          inspectionType: c.inspectionType,
          address: c.address,
          customerName: c.customerName,
        } as FieldServicesCaseResult)).sort(caseSort);
        setEmployeeCaseQueue(cases);
      }, mounted);
    } else if (callRole.roleDto.name === "Quality Review") {
      getUserAuditResults({ internetUserId: selectedEmployee.internetUserId, lookbackDays: 14 }, (auditResults: QaAgentAuditResultResponse[]) => {
        getCasesInQueue(selectedEmployee.employeeId, (casesInQueue: FieldServicesCaseResult[]) => {
          let allCases = auditResults.map(c => ({
            controlIDNumber: c.controlIdNumber,
            inspectionType: c.surveyType,
            address: c.address,
            customerName: c.customerInfo.customerName,
          } as FieldServicesCaseResult)).concat(casesInQueue);
          allCases.sort(caseSort);
          setEmployeeCaseQueue(allCases);
        }, mounted);
      }, mounted);
    }
  };

  const setHasChangedData = (hasChanges: boolean) => {
    if (hasChanges) {
      // @ts-ignore
      window.onbeforeunload = () => true;
      if (!historyBlockRef.current) {
        historyBlockRef.current = history.block(newLocation => {
          if (location.pathname !== newLocation.pathname) {
            return "You have unsaved changes. Are you sure you want to leave this page?";
          }
        });
      }
    } else {
      // @ts-ignore
      window.onbeforeunload = null;
      historyBlockRef.current?.();
      historyBlockRef.current = null;
    }
  };

  // see comment on callCardKey useState line
  const refreshCallCard = () => setCallCardKey(`key-${Math.random()}`);

  const getEmployeeRoleFromRoleName = (roleName: string) => roleToEmployeeRoleMap.find(r => r.roleName === roleName)?.employeeRoleCode;

  handleSaveClicked.current = () => {
    if (isSavingCall) return;
    
    if (!startedWorkingOnCall) {
      dialog.showOk("Can't Save Case", "You need to start a call before you can save it");
      return;
    }

    if (!callCases?.length) {
      dialog.showOk("No Cases on Call", "You must search at least one case before you can save the call");
      return;
    }

    let allCasesHaveNotes = !callCases.some(cc => !cc.notes);

    if (!allCasesHaveNotes) {
      dialog.showOk("Cases Missing Notes", "You must provide notes for all cases on the call");
      return;
    }
    
    if (cardControlMethods.current?.runValidation(true)) {
      setIsSavingCall(true);
      setSearchForControlNumber(null);
      let phoneSupportCall: PhoneSupportCallAddRequest = {
        supportedForRoleId: callRole.roleDto.id,
        phoneSupportCategoryId: selectedCategory.id,
        supportedEmployeeId: selectedEmployee.employeeId,
        supportedCallerName: selectedEmployee.firstLastName ?? callerName,
        startUtcDateTime: startedWorkingOnCall,
        completeUtcDateTime: new Date(),
        timeZoneOffsetMinutes: new Date().getTimezoneOffset(),
        notes: notes,
        isCompleted: true,
      };

      addPhoneSupportCall(phoneSupportCall, async newId => {
        await Promise.all(callCases.map(cc => {
          cc.phoneSupportCallId = newId;

          return addPhoneSupportCallCase(cc, null, mounted);
        }));

        dialog.showYesNo(
          "Call Saved Successfully",
          "Is the current caller calling about any other categories? Select Yes to log another call for the same caller.",
          () => resetPageState(true),
          () => resetPageState(false),
        );
      }, mounted);
    }
  };

  const resetPageState = (sameCaller: boolean) => {
    setIsSavingCall(false);
    setSelectedCategory(null);
    setNotes("");
    setControlNumberLookup("");
    setSearchForControlNumber(null);
    setCallCases([]);
    refreshCallCard();
    setStartedWorkingOnCall(sameCaller ? new Date() : null);
    
    if (!sameCaller) {
      setEmployeeCaseQueue(null);
      setHasChangedData(false);
      setCallRole(null);
      setSelectedEmployee(null);
      setCallerName("");
    }
  };

  const startCall = () => {
    setStartedWorkingOnCall(new Date());
  };

  const appendSaveHeaderButton = () => {
    appendHeader({
      buttons: [
        { name: "Save", onClick: () => handleSaveClicked.current() }
      ],
    });
  };

  const onCasePaneClosed = () => {
    setSearchForControlNumber(null);
    clearHeaderDefinitions(saveButtonForClear);
    appendSaveHeaderButton();
    setControlNumberLookup("");
  };

  const onCaseCardChanged = (controlIdNumber: number, changeArray) => {
    changeArray.forEach(({ key, value, originalValue }) => {
      if (key === "notes") {
        let callCasesNew = [...callCases];
        callCasesNew.find(cc => cc.controlIdNumber === controlIdNumber).notes = value;
        setCallCases(callCasesNew);
      }
    });
  };

  const removeCallCase = (controlIdNumber: number, areYouSure?: boolean) => {
    if (areYouSure === true) {
      setCallCases(callCases.filter(cc => cc.controlIdNumber !== controlIdNumber));

      if (controlIdNumber === searchForControlNumber) onCasePaneClosed();
    } else {
      dialog.showYesNo("Are you sure", `Are you sure you want to remove your notes for Case ${controlIdNumber}?`, () => removeCallCase(controlIdNumber, true), () => dialog.hide());
    }
  };

  const onCaseSearchClicked = (controlNumberOverride?: number) => {
    let numericControlNumber: number;
    if (controlNumberOverride) {
      setControlNumberLookup(`${controlNumberOverride}`);
      numericControlNumber = controlNumberOverride;
    } else {
      numericControlNumber = +controlNumberLookup?.trim();
    }

    if (numericControlNumber > 10000000) {
      setSearchForControlNumber(numericControlNumber);
      if (!callCases.some(c => c.controlIdNumber === numericControlNumber)) {
        setCallCases([{ controlIdNumber: numericControlNumber, notes: "" } as PhoneSupportCallCaseAddRequest, ...callCases]);
      }
    } else {
      dialog.showOk("Invalid Control Number", "Please enter an 8 digit control number");
    }
  };

  const mapPhoneCategory = (category: PhoneSupportCategoryDto) => category && ({ label: category.name, value: category });

  const mapEmployeeForDropdown = (e: EmployeeListDto) => ({ label: e.firstLastName + (e.phoneNumber ? ` (${normalizePhone(e.phoneNumber)})` : " (phone # not provided)"), value: e });

  const onCallerRoleChange = (newRole) => {
    setCallRole({ roleDto: newRole.value, employeeRoleCode: getEmployeeRoleFromRoleName(newRole.value.name) });
    setSelectedEmployee(null);
    setCallerName(null);
    refreshCallCard();
  };

  const onSelectedEmployeeChange = (newEmployee) => {
    setSelectedEmployee(newEmployee.value);
  };

  const onCategoryChange = (newCategory) => {
    setSelectedCategory(newCategory.value);
  }

  let supportForm = <MSpinner />;

  if (roles && employeesByEmployeeRoleId && phoneSupportCategories && !isSavingCall) {

    let rolesArray = roles.map(r => ({ label: r.name, value: r }));

    let callCardFields: MCardField[] = [
      {
        key: "callRole",
        label: "Caller Role",
        validation: yup.string().nullable().required("Caller Role is required"),
        value: rolesArray.find(r => r.value.name === callRole?.roleDto.name), dataType: DATA_TYPE_DROPDOWN2, dataTypeOptions: { options: rolesArray, nullable: false },
        onChange: onCallerRoleChange,
      },
    ];

    if (includedRoleNames.includes(callRole?.roleDto.name)) {
      callCardFields.push({
        key: "employee",
        label: "Employee Calling",
        validation: yup.string().nullable().required("Employee Calling is required"),
        value: selectedEmployee && mapEmployeeForDropdown(selectedEmployee), // need to provide employee value because the page could be reset with the employee still selected if we are doing another call for the same person
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: employeesByEmployeeRoleId[callRole.employeeRoleCode].map(mapEmployeeForDropdown), nullable: false },
        onChange: onSelectedEmployeeChange,
      },
        
      );
    } else {
      callCardFields.push({ key: "callerName", label: "Caller Name", validation: yup.string().nullable().required("Caller Name is required") });
    }

    callCardFields.push(
      {
        key: "category",
        label: "Category",
        validation: yup.string().nullable().required("Category is required"),
        value: mapPhoneCategory(selectedCategory),
        dataType: DATA_TYPE_DROPDOWN2,
        dataTypeOptions: { options: phoneSupportCategories.map(c => mapPhoneCategory(c)), nullable: false },
        onChange: onCategoryChange,
      },
    );

    let employeeCaseGrid: ReactNode = null;

    if (callRole && selectedEmployee) {
      employeeCaseGrid = (
        <MGrid
          className="md-paper--1"
          isFetchingData={isLoadingCaseQueue}
          data={employeeCaseQueue}
          columnDefinitions={[
            { code: COL_CODE_CONTROL_NUMBER, dataSource: "controlIDNumber" },
            { key: "inspectionType", width: 270, name: "Inspection Type", dataSource: "inspectionType" },
            { code: COL_CODE_ADDRESS, dataSource: "address", name: "Address to Survey" },
            { code: COL_CODE_CUSTOMER_NAME, dataSource: "customerName" },
          ]}
          onRowClick={(index: number) => onCaseSearchClicked(employeeCaseQueue[index].controlIDNumber)}
        />
      );
    }

    supportForm = (
      <div className="phone-support-form">
        <MCard
          key={callCardKey}
          title="Call"
          onlyEditable
          fields={callCardFields}
          getControlMethods={controlMethods => cardControlMethods.current = controlMethods}
        />
        <div className="go-to-case md-paper--1">
          <div className="case-lookup-label">Case Lookup: </div>
          <TextField
            className="control-number-input"
            onChange={newValue => setControlNumberLookup(`${newValue}`)}
            value={controlNumberLookup}
          />
          <Button primary iconEl={<i className="md-icon material-icons">search</i>} raised onClick={() => onCaseSearchClicked()}>Search</Button>
        </div>
        {callCases.map(cc => (
          <MCard
            key={`control${cc.controlIdNumber}`}
            onlyEditable
            title={`Notes for Control Number ${cc.controlIdNumber}`}
            fields={[
              {
                key: "notes",
                label: "Notes",
                dataType: DATA_TYPE_MULTI_LINE_STRING,
                value: cc.notes,
                validation: yup.string().required("Notes are required"),
              }
            ]}
            onEditChanged={changeArray => onCaseCardChanged(cc.controlIdNumber, changeArray)}
            actions={[
              { icon: "search", onClick: () => { setControlNumberLookup(`${cc.controlIdNumber}`); setSearchForControlNumber(cc.controlIdNumber); } },
              { icon: "close", onClick: () => removeCallCase(cc.controlIdNumber) },
            ]}
          />
        ))}
        {employeeCaseGrid}
      </div>
    );
  }

  let caseView: ReactNode = null;

  if (searchForControlNumber) {
    // change the key because MCaseView probably isnt the best with control numbers getting switched out from under it
    caseView = <MCaseView key={`${searchForControlNumber}`} controlNumberOverride={searchForControlNumber} />;
  }

  return (
    <div className="phone-support-form-container">
      {startedWorkingOnCall ?
      <MSplitter
        firstChild={supportForm}
        firstChildInitialSize="var(--phone-support-form-max-width)" // defined in PhoneSupportForm.scss
        secondChildTitle={{
          title: `Case View for ${searchForControlNumber}`,
          buttons: [
            { icon: "close", onClick: onCasePaneClosed }
          ]
        }}
        secondChild={caseView}
      /> :
      <Button primary raised onClick={startCall}>Start</Button>
      }
    </div>
  );
};

const mapStateToProps = (state: InitialState) => ({
  canClockInOut: userHasRight(CLOCK_IN_OR_OUT_FUNCTION)(state),
  activeClock: state.user.activeClock,
});

const mapDispatchToProps = dispatch => ({
  appendHeader(headerDefinitions) {
    dispatch(appendHeader(headerDefinitions));
  },
  clearHeaderDefinitions(headerDefinitions) {
    dispatch(clearHeaderDefinitions(headerDefinitions));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PhoneSupportForm));
